<template>
  <div>
    <div class="is-flex is-justify-content-space-between" style="width: 100%">
      <label class="label">Struktur</label>
      <div style="margin-top: -8px">
        <slot name="header-action" />
      </div>
    </div>
    <div class="route-tree-wrapper p-3">
      <Tree
        :value="treeModel"
        :indent="70"
        :draggable="draggable"
        triggerClass="drag-handler"
        @change="onTreeChange"
        @drop="onDrop"
        style="overflow-y: auto"
        :style="{ height: height }"
      >
        <div slot-scope="{ node, path, tree, index }" class="is-flex pt-1">
          <a
            href="#"
            :style="{ visibility: isVisible(node) }"
            @click.prevent="tree.toggleFold(node, path)"
          >
            <b-icon :icon="setArrow(node)" />
          </a>
          <b-icon
            v-if="node.icon"
            :icon="node.icon"
            type="is-grey"
            class="ml-1"
          />
          <div class="ml-2">
            {{ node.name }}
          </div>
          <div style="margin-left: auto">{{ node.pos }} | {{ index }}</div>
          <div class="ml-5">
            <slot
              name="item-action"
              :route="node"
              :tree="tree"
              :path="path"
              :select="select"
            />
          </div>
        </div>
      </Tree>
    </div>
    <slot name="add-button" :isCreating="isCreating" />
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import { mapGetters } from "vuex";
import "he-tree-vue/dist/he-tree-vue.css";
import {
  Tree,
  Draggable,
  Fold,
  cloneTreeData,
  walkTreeData
} from "he-tree-vue";

export default {
  name: "RouteTree",
  props: {
    productId: {
      type: Number,
      required: true
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false
    },
    height: {
      type: String,
      required: false,
      default: "initial"
    },
    highlightOnSelect: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  components: {
    Tree: Tree.mixPlugins([Fold, Draggable])
  },
  data() {
    return {
      treeModel: [],
      tree: null,
      prevState: null,
      selectedRoute: null,
      selectedPath: null,
      creating: false,
      editing: false,
      ordering: false
    };
  },
  computed: {
    ...mapGetters({
      getRoutes: "product/route/dataByParentId",
      error: "product/route/error"
    }),
    // Required for detecting a newly created route
    routes() {
      return this.getRoutes(this.productId);
    },
    routesById() {
      return this.routes.reduce((acc, route) => {
        acc[route.id] = route;
        return acc;
      }, {});
    },
    // Transform list of routes into tree of routes
    routeTree() {
      return this.listToTree(this.routes);
    }
  },
  watch: {
    productId() {
      this.treeModel = cloneTreeData(this.routeTree);
      this.creating = false;
      this.editing = false;
      this.ordering = false;
    },
    routes(newVal, oldVal) {
      // Watch for a newly created route
      if (this.creating && newVal.length > oldVal.length) {
        const newRoute = newVal[newVal.length - 1];

        this.treeModel.unshift({
          $nodeClass: "new",
          ...newRoute
        });

        walkTreeData(this.treeModel, this.updateRoute);
      }

      // Watch for updated route
      if (newVal.length === oldVal.length) {
        if (this.editing) {
          this.updateRoute(this.selectedRoute);
        }

        if (this.ordering) {
          walkTreeData(this.treeModel, this.updateRoute);
        }
      }

      // Watch for the deleted route
      if (this.editing && newVal.length < oldVal.length) {
        this.tree.removeNodeByPath(this.selectedPath);
      }
    },
    routeTree(newVal, oldVal) {
      // treeModel is set here in case the user is refreshing the page
      if (oldVal.length === 0) {
        this.treeModel = cloneTreeData(newVal);
      }
    },
    selectedRoute: {
      deep: true,
      handler(newVal, oldVal) {
        if (oldVal && oldVal.id !== newVal.id) {
          this.unhighlight(oldVal);
        }
      }
    },
    error() {
      if (this.ordering) {
        walkTreeData(this.prevState, this.unhighlight);
        this.treeModel = cloneTreeData(this.prevState);
      }
    }
  },
  methods: {
    cloneTreeData,
    onTreeChange(e) {
      let node = null;
      let newPos = null;
      let parent = null;

      function walkTreeHandler(_node, _index, _parent) {
        if (_node.id !== e.dragNode.id) return true;
        node = _node;
        parent = _parent;
        newPos = _index;
        return false;
      }

      e.targetTree.walkTreeData(walkTreeHandler);
      const parentId = parent ? parent.id : null;

      this.$emit("change", {
        id: node.id,
        parentId,
        newPos
      });
    },
    isCreating() {
      this.creating = true;
      if (this.selectedRoute) {
        this.unhighlight(this.selectedRoute);
      }
    },
    highlight(node) {
      node.$nodeClass = "highlighted";
    },
    unhighlight(node) {
      node.$nodeClass = "";
    },
    listToTree(list) {
      let routeTree = [];

      function getChildren(parentId) {
        const rootTree = list
          .filter(route => route.parent_id === parentId)
          .sort((a, b) => a.pos - b.pos);

        const output = [];

        rootTree.forEach(root => {
          const routeCopy = { ...root };
          routeCopy.children = getChildren(root.id);
          routeCopy.$nodeClass = "";
          routeCopy.$folded = true;
          output.push(routeCopy);
        });

        return output;
      }

      list.forEach(() => {
        routeTree = getChildren(null);
      });

      return routeTree;
    },
    select({ tree, route, path, mode }) {
      if (this.highlightOnSelect) {
        this.highlight(route);
      }

      this.editing = mode === "edit";
      this.ordering = mode === "move";
      this.prevState = cloneTreeData(this.treeModel);
      this.tree = tree;
      this.selectedRoute = route;
      this.selectedPath = path;

      const { $folded, $nodeClass, ...routeData } = route;
      this.$emit("selected", routeData);
    },
    isVisible(node) {
      return node.children && node.children.length ? "visible" : "hidden";
    },
    setArrow(node) {
      return node.$folded ? "angle-right" : "angle-down";
    },
    updateRoute(node) {
      // Need to be assigned manually
      // Object.assign will cause unexpected behavior
      const updated = this.routesById[node.id];
      node.name = updated.name;
      node.icon = updated.icon;
      node.link = updated.link;
      node.pos = updated.pos;
      node.label = updated.label;
      node.filter = updated.filter;
      node.tags = updated.tags;
      node.default_visible = updated.default_visible;
      node.parent_id = updated.parent_id;
    },
    onDrop(e) {
      this.unhighlight(e.dragNode);
    }
  },
  created() {
    this.treeModel = cloneTreeData(this.routeTree);
  },
  updated() {
    this.$nextTick(() => {
      // Scroll to the newly added route
      const newTreeItem = document.querySelector(".tree-node.new");
      if (!newTreeItem) return;
      newTreeItem.scrollIntoView({ behavior: "smooth" });
      // Highlight the newly created route
      newTreeItem.classList.add("highlighted");
      // Reset the newly created route's class
      setTimeout(() => {
        newTreeItem.classList.remove("highlighted", "new");
      }, 2500);

      this.creating = false;
    });
  }
};
</script>

<style>
.route-tree-wrapper {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}
.route-tree-wrapper .he-tree .tree-node {
  border-top: none;
  border-right: none;
  border-left: none;
  border-bottom: 1px solid #dbdbdb;
  margin-bottom: 0;
}
.route-tree-wrapper .tree-node.highlighted {
  background-color: #eff2f5; /* #9c7b4d */
}
</style>
